if (!customElements.get("cn-pdp-sticky-atc")) {
  customElements.define(
    "cn-pdp-sticky-atc",
    class CnPdpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.stickyButton = this.querySelector(".cn-pdp-sticky-atc__button");
        this.stickyLabel = this.querySelector(".cn-pdp-sticky-atc__label");
        this.stickyPriceEl = this.querySelector(".cn-pdp-sticky-atc__price");
        this.stickyQtyVal = this.querySelector(".cn-pdp-sticky-atc__qty-val");

        if (!this.productInfo || !this.mainButton || !this.stickyButton || !this.stickyLabel) return;

        this.productForm = this.productInfo.querySelector('product-form form[data-type="add-to-cart-form"]');
        this.mainButtonText = this.mainButton.querySelector("span:not(.loading__spinner):not(.sold-out-message)");
        this.mainCtaVisible = true;
        this.nearFooter = false;
        this.isDelegating = false;

        this.onStickyClick = this.onStickyClick.bind(this);
        this.syncFromMain = this.syncFromMain.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);

        this.stickyButton.addEventListener("click", this.onStickyClick);

        // Qty ± buttons wire into main quantity input
        this.querySelector('[data-action="minus"]')?.addEventListener("click", () => this.adjustQty(-1));
        this.querySelector('[data-action="plus"]')?.addEventListener("click", () => this.adjustQty(1));

        this.ctaObserver = new IntersectionObserver(
          (entries) => {
            this.mainCtaVisible = entries.some((entry) => entry.isIntersecting);
            this.updateVisibility();
          },
          { threshold: [0, 0.01] },
        );
        this.ctaObserver.observe(this.mainButton);

        const footerTargets = [
          document.querySelector(".footer__support"),
          document.querySelector(".footer__content-top"),
          document.querySelector("footer.footer"),
        ].filter(Boolean);

        if (footerTargets.length) {
          this.footerObserver = new IntersectionObserver(
            (entries) => {
              this.nearFooter = entries.some((entry) => entry.isIntersecting);
              this.updateVisibility();
            },
            { threshold: [0, 0.01] },
          );
          footerTargets.forEach((target) => this.footerObserver.observe(target));
        }

        this.mainObserver = new MutationObserver(this.syncFromMain);
        this.mainObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ["disabled", "aria-disabled", "class"],
          childList: true,
          subtree: true,
          characterData: true,
        });

        const quantityInput = this.productInfo.querySelector('.quantity__input[name="quantity"]');
        quantityInput?.addEventListener("change", this.syncFromMain);
        quantityInput?.addEventListener("input", this.syncFromMain);

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          this.syncFromMain();
        });

        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          this.syncStickyQtyButtons();
        });

        const onProductInfoLoaded = (event) => {
          if (!this.productInfo.contains(event.target) && event.target !== this.productInfo) return;
          this.productForm = this.productInfo.querySelector('product-form form[data-type="add-to-cart-form"]');
          this.syncFromMain();
        };
        this.productInfo.addEventListener("product-info:loaded", onProductInfoLoaded);

        this.syncFromMain();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.stickyButton?.removeEventListener("click", this.onStickyClick);
        this.ctaObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.mainObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
      }

      syncStickyQtyButtons() {
        const qtyInput = this.productInfo?.querySelector('.quantity__input[name="quantity"]');
        if (!qtyInput) return;

        const value = parseInt(qtyInput.value) || 1;
        const min = parseInt(qtyInput.min) || 1;
        const max = qtyInput.max !== "" && qtyInput.max != null ? parseInt(qtyInput.max) : null;

        const plusBtn = this.querySelector('[data-action="plus"]');
        const minusBtn = this.querySelector('[data-action="minus"]');

        if (plusBtn) {
          // Visual-only: keep clickable so the user gets a toast on tap instead of silence.
          const atMax = max !== null && value >= max;
          plusBtn.classList.toggle("disabled", atMax);
          plusBtn.disabled = false;
        }
        if (minusBtn) {
          const atMin = value <= min;
          minusBtn.disabled = atMin;
          minusBtn.classList.toggle("disabled", atMin);
        }
      }

      adjustQty(delta) {
        const qtyInput = this.productInfo.querySelector('.quantity__input[name="quantity"]');
        if (!qtyInput) return;

        const current = parseInt(qtyInput.value) || 1;
        const min = parseInt(qtyInput.min) || 1;
        const max = qtyInput.max !== "" && qtyInput.max != null ? parseInt(qtyInput.max) : null;
        const step = parseInt(qtyInput.step) || 1;

        // If tapping + at max, show the limit toast and don't increment
        if (delta > 0 && max !== null && current >= max) {
          const qtyEl = qtyInput.closest("quantity-input");
          if (qtyEl && typeof qtyEl.buildLimitMessage === "function") {
            const msg = qtyEl.buildLimitMessage();
            if (msg && typeof qtyEl.showQuantityLimitMessage === "function") {
              qtyEl.showQuantityLimitMessage(msg);
            }
          }
          return;
        }

        const next =
          max !== null ? Math.min(max, Math.max(min, current + delta * step)) : Math.max(min, current + delta * step);

        if (next !== current) {
          qtyInput.value = next;
          qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      onStickyClick(event) {
        event.preventDefault();
        if (this.isDelegating || this.isMainSubmitBlocked()) return;

        this.syncQuantityToForm();
        this.isDelegating = true;

        try {
          this.mainButton.click();
        } finally {
          window.setTimeout(() => {
            this.isDelegating = false;
            this.syncFromMain();
          }, 0);
        }
      }

      isMainSubmitBlocked() {
        return (
          this.mainButton.disabled ||
          this.mainButton.getAttribute("aria-disabled") === "true" ||
          this.mainButton.classList.contains("loading")
        );
      }

      syncQuantityToForm() {
        if (!this.productForm) return;

        const qtyInput = this.productInfo.querySelector('.quantity__input[name="quantity"]');
        const quantityValue = qtyInput?.value || qtyInput?.min || "1";
        let hiddenQty = this.productForm.querySelector('input[type="hidden"][name="quantity"]');

        if (!hiddenQty) {
          hiddenQty = document.createElement("input");
          hiddenQty.type = "hidden";
          hiddenQty.name = "quantity";
          this.productForm.appendChild(hiddenQty);
        }

        hiddenQty.value = quantityValue;
      }

      getVisiblePriceText() {
        const priceRoot = this.productInfo.querySelector(`#price-${this.sectionId}`);
        if (!priceRoot || priceRoot.classList.contains("hidden")) return "";

        const onSale = priceRoot.classList.contains("price--on-sale");
        const priceItem = onSale
          ? priceRoot.querySelector(".price-item--sale.price-item--last")
          : priceRoot.querySelector(".price__regular .price-item--regular");

        return priceItem?.textContent?.replace(/\s+/g, " ").trim() || "";
      }

      syncFromMain() {
        if (!this.mainButton || !this.stickyLabel || !this.stickyButton) return;

        const mainLabel = this.mainButtonText?.textContent?.replace(/\s+/g, " ").trim() || "";
        const priceText = this.getVisiblePriceText();
        const variantId = this.productForm?.querySelector('[name="id"]')?.value;
        const addToCartLabel = window.variantStrings?.addToCart || "Add to cart";
        const isAddToCartState =
          mainLabel.toLowerCase() === addToCartLabel.toLowerCase() || mainLabel.toLowerCase() === "add to cart";

        if (variantId) this.dataset.variantId = variantId;

        // Price element (new row layout)
        if (this.stickyPriceEl) {
          this.stickyPriceEl.textContent = priceText;
        }

        // Button label — just "Add to Cart" now; price is in its own element
        if (isAddToCartState) {
          this.stickyLabel.textContent = addToCartLabel;
        } else if (mainLabel) {
          this.stickyLabel.textContent = mainLabel;
        }

        // Quantity display
        if (this.stickyQtyVal) {
          const qtyInput = this.productInfo.querySelector('.quantity__input[name="quantity"]');
          this.stickyQtyVal.textContent = qtyInput?.value || "1";
        }

        const blocked = this.isMainSubmitBlocked();
        this.stickyButton.disabled = blocked;
        this.stickyButton.classList.toggle("loading", this.mainButton.classList.contains("loading"));
        this.stickyButton.setAttribute("aria-disabled", blocked ? "true" : "false");
        this.stickyButton.setAttribute(
          "aria-label",
          isAddToCartState && priceText ? `${addToCartLabel}, ${priceText}` : mainLabel || addToCartLabel,
        );

        this.syncStickyQtyButtons();
      }

      updateVisibility() {
        const shouldShow = !this.mainCtaVisible && !this.nearFooter;

        this.classList.toggle("cn-pdp-sticky-atc--visible", shouldShow);
        this.setAttribute("aria-hidden", shouldShow ? "false" : "true");
        // Drive toast positioning: toast shifts up above sticky when visible
        document.documentElement.classList.toggle("cn-sticky-atc-visible", shouldShow);
      }
    },
  );
}
